/**
 * API Response Types - Trade Loop v2.0
 *
 * Standard response structures from the API
 * Ensures type safety and consistency across the application
 */

/**
 * Standard API response wrapper
 * All API endpoints return data in this format
 */
export interface ApiResponse<T> {
  data: T;
  success: boolean;
  error?: string;
}

/**
 * Referral interface (camelCase for frontend)
 */
export interface Referral {
  id: number | string;
  senderId: string;
  receiverId: string;
  senderName?: string;
  senderTrade?: string;
  senderProfileImage?: string;
  receiverName?: string;
  receiverTrade?: string;
  receiverProfileImage?: string;
  // Bug 23: the other party permanently deleted their account. The referral (and its revenue)
  // survives; senderName/receiverName carry the neutral tombstone label and there is no profile
  // left to open.
  senderDeleted?: boolean;
  receiverDeleted?: boolean;
  senderFormerCompany?: string | null;
  receiverFormerCompany?: string | null;
  clientName: string;
  clientEmail?: string;
  clientPhone?: string;
  jobDescription: string;
  jobLocation: string;
  revenue?: number;
  status: 'pending' | 'accepted' | 'declined' | 'completed' | 'expired' | 'canceled';
  notes?: string;
  attachments?: string | null;
  hiddenFor?: string[];
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
  // Group referral fields
  referral_group_id?: string | null;
  // Additional fields
  trades?: string[];
  urgencyLevel?: string;
  // Group metadata (added when displaying grouped referrals)
  _isGroupRepresentative?: boolean;
  _groupSize?: number;
  _groupReferrals?: Referral[];
}

/**
 * Dashboard stats response
 */
export interface DashboardStats {
  activeReferrals: number;
  monthlyEarnings: number;
  partnerConnections: number;
  referralsThisMonth: number;
  subscriptionPlan: 'starter' | 'core' | 'pro' | 'grow';
  subscriptionStatus: 'active' | 'past_due' | 'canceled' | 'paused';
  planLimits: {
    monthlyReferrals: number;
    totalConnections: number;
  };
}

/**
 * Connection interface (camelCase for frontend)
 * Includes both connection data and connected user's profile data
 */
export interface Connection {
  id: number | string;
  userId: string;
  connectedUserId: string;
  status: 'pending' | 'accepted' | 'declined' | 'blocked';
  connectionMethod?: string;
  isFavorite: boolean;
  lastContactSync?: string;
  createdAt: string;
  updatedAt: string;
  // Connected user's profile fields (from joined user_profiles table)
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  trade?: string;
  profileImageUrl?: string;
  company?: string;
}

/**
 * Notification interface (camelCase for frontend)
 */
export interface Notification {
  id: number | string;
  userId: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error' | 'referral' | 'connection' | 'message';
  readAt?: string;
  createdAt: string;
  // Optional metadata
  referralId?: string;
  partnerId?: string;
  conversationId?: string;
}

/**
 * Chat message interface (camelCase for frontend)
 */
export interface ChatMessage {
  id: number | string;
  chatId: string;
  senderId: string;
  recipientId: string;
  messageContent: string;
  messageType: 'text' | 'image' | 'file' | 'voice';
  attachmentUrl?: string;
  readAt?: string;
  createdAt: string;
  updatedAt: string;
  // Extended fields from joined user profile
  senderName?: string;
  senderProfileImage?: string;
}

/**
 * User profile interface (camelCase for frontend)
 */
export interface UserProfile {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  profileImageUrl?: string;
  trade?: string;
  company?: string;
  bio?: string;
  location?: string;
  website?: string;
  linkedinUrl?: string;
  createdAt: string;
  updatedAt: string;
  sessionValidAfter?: string;
  primaryTradeId?: string;
  primaryTradeCategoryName?: string;
}

/**
 * Type guard to check if data is an API response
 */
export function isApiResponse<T>(data: any): data is ApiResponse<T> {
  return (
    data &&
    typeof data === 'object' &&
    'success' in data &&
    typeof data.success === 'boolean' &&
    'data' in data
  );
}

/**
 * Helper to extract data from API response
 * Returns empty array if data is not valid
 */
export function extractArrayData<T>(response: unknown): T[] {
  if (isApiResponse<T[]>(response)) {
    return Array.isArray(response.data) ? response.data : [];
  }
  return [];
}

/**
 * Helper to extract single item from API response
 * Returns undefined if data is not valid
 */
export function extractData<T>(response: unknown): T | undefined {
  if (isApiResponse<T>(response)) {
    return response.data;
  }
  return undefined;
}
